// @documentacao/ui/IconRodo.tsx
"use client";

import * as React from "react";
import { useEffect, useRef } from "react";
import { animate, stagger } from "animejs";

interface IconRodoProps {
  size?: number | string;
  className?: string;
  speed?: number; // Multiplicador de velocidade (1 = normal, 0.5 = 2x mais rápido, 2 = 2x mais lento)
}

export const Loader: React.FC<IconRodoProps> = ({
  size = 32,
  className = "",
  speed = 1,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const fill = "var(--primary)";
  // Se for número ou string numérica, converte para "px"
  const dimension =
    typeof size === "number" || /^\d+$/.test(String(size))
      ? `${size}px`
      : String(size);

  // useEffect para inicialização e configuração inicial
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const centerBall = svg.querySelector("path:nth-child(1)") as SVGPathElement;
    const satellites = svg.querySelectorAll("path:not(:nth-child(1))");

    // Calcula o centro real baseado nas coordenadas do círculo central atualizado
    // O círculo central vai aproximadamente de X=162 a X=275 e Y=148 a Y=263
    const centerX = (162 + 275) / 2; // 218.5
    const centerY = (148 + 263) / 2; // 205.5

    if (centerBall) {
      // Define o ponto de origem da transformação como o centro real do círculo central
      centerBall.style.transformOrigin = `${centerX}px ${centerY}px`;
      // SEMPRE inicia com tamanho 0 - estado inicial garantido
      centerBall.style.transform = "scale(0)";
      centerBall.style.opacity = "1"; // Garante que está visível mas em tamanho 0
    }

    // Torna o SVG visível antes de iniciar as animações
    if (svg) {
      svg.style.opacity = "1";
    }

    // Prepara todos os satélites
    satellites.forEach((satellite) => {
      const svgSatellite = satellite as SVGPathElement;
      svgSatellite.style.opacity = "0";
      svgSatellite.style.transform = "scale(0)";
      svgSatellite.style.transformOrigin = "center";
    });

    // Adiciona os keyframes CSS dinamicamente
    if (!document.getElementById("pulse-keyframes")) {
      const style = document.createElement("style");
      style.id = "pulse-keyframes";
      style.textContent = `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // useEffect para controlar o looping contínuo
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const centerBall = svg.querySelector("path:nth-child(1)") as SVGPathElement;
    const satellites = svg.querySelectorAll("path:not(:nth-child(1))");

    // Função para iniciar uma animação completa
    const startAnimation = () => {
      if (!centerBall) return;

      // Função para animar o círculo central (aparecer)
      const animateCenterAppear = () => {
        if (!centerBall) return;

        const animation = animate(centerBall, {
          scale: [0, 1.1, 1], // Bounce mais sutil
          duration: 600 / speed, // Reduzido de 1200ms para 600ms
          easing: "easeOutElastic(1, 0.5)", // Elastic mais suave
        });

        return animation;
      };

      // Função para animar o círculo central (desaparecer)
      const animateCenterDisappear = () => {
        if (!centerBall) return;

        const animation = animate(centerBall, {
          scale: [1, 1.05, 0], // Bounce mais sutil
          duration: 500 / speed, // Reduzido de 1000ms para 500ms
          easing: "easeInElastic(1, 0.5)", // Elastic mais suave
        });

        return animation;
      };

      // Inicia a animação
      animateCenterAppear();

      // Anima os satélites
      if (satellites.length > 0) {
        satellites.forEach((satellite) => {
          const svgSatellite = satellite as SVGPathElement;
          svgSatellite.style.opacity = "0";
          svgSatellite.style.transform = "scale(0)";
          svgSatellite.style.transformOrigin = "center";
        });

        const animation = animate(satellites, {
          opacity: [0, 1],
          scale: [0, 1],
          rotate: [0, 360],
          delay: stagger(100 / speed), // Reduzido de 200ms para 100ms
          duration: 500 / speed, // Reduzido de 1000ms para 500ms
          easing: "easeOutBack",
        });

        // Timing para o desaparecimento
        const lastSatelliteDelay = (satellites.length * 100 + 500) / speed; // Atualizado para os novos tempos
        const lastSatelliteFinishDelay = lastSatelliteDelay;

        setTimeout(() => {
          animate(satellites, {
            opacity: [1, 0],
            scale: [1, 0],
            rotate: [0, 360],
            delay: stagger(100 / speed, { from: "last" }), // Reduzido de 200ms para 100ms
            duration: 500 / speed, // Reduzido de 1000ms para 500ms
            easing: "easeInBack",
          });

          setTimeout(() => {
            animateCenterDisappear();

            // Após terminar, reinicia automaticamente (looping contínuo)
            setTimeout(() => {
              startAnimation(); // Reinicia a animação
            }, 500 / speed); // Reduzido de 1000ms para 500ms
          }, (satellites.length * 100) / speed); // Reduzido de 200ms para 100ms
        }, lastSatelliteFinishDelay);
      }
    };

    // Inicia o looping contínuo
    startAnimation();
  }, [speed]);
  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 434 405"
      width={dimension}
      height={dimension}
      className={className}
      style={{ opacity: 0 }}
    >
      {/* Bola central (empresa) */}
      <path
        fill={fill}
        d="M186.784 148.757C197.316 142.905 209.335 140.269 221.351 141.174C233.369 142.081 244.854 146.49 254.389 153.856C263.923 161.221 271.084 171.22 274.988 182.613C278.892 194.007 279.365 206.293 276.349 217.952C273.334 229.612 266.961 240.131 258.023 248.207C249.084 256.282 237.97 261.561 226.059 263.388C214.148 265.215 201.961 263.509 191.01 258.484C180.059 253.458 170.824 245.332 164.448 235.113L162.885 232.461L161.455 229.734C154.476 215.566 153.214 199.26 157.928 184.188C162.642 169.115 172.973 156.431 186.784 148.757Z"
      />

      {/* Satélite 1 - Semicírculo (superior direito) */}
      <path
        fill={fill}
        d="M90.3202 276.665C91.9886 275.523 93.8658 274.72 95.8445 274.303C97.8233 273.886 99.8649 273.863 101.853 274.234C103.841 274.606 105.736 275.365 107.43 276.468C109.124 277.572 110.584 278.998 111.727 280.665C123.468 297.8 139.356 311.688 157.913 321.036C176.469 330.385 197.09 334.89 217.857 334.134C219.88 334.059 221.896 334.382 223.793 335.086C225.689 335.79 227.428 336.86 228.911 338.235C230.394 339.611 231.591 341.265 232.434 343.102C233.277 344.94 233.75 346.926 233.825 348.947C233.9 350.967 233.577 352.982 232.873 354.877C232.169 356.772 231.097 358.51 229.721 359.992C228.345 361.473 226.689 362.67 224.85 363.512C223.011 364.355 221.024 364.827 219.003 364.902C193.039 365.85 167.258 360.219 144.057 348.532C120.858 336.843 100.995 319.48 86.3175 298.056C84.0143 294.69 83.1429 290.546 83.8948 286.538C84.6467 282.529 86.9604 278.983 90.3275 276.678L90.3202 276.665Z"
      />

      {/* Satélite 2 - Triângulo (superior direito segundo) */}
      <path
        fill={fill}
        d="M287.632 126.608C283.437 128.261 279.022 124.763 279.677 120.304L288.272 61.8032C288.929 57.3356 294.181 55.256 297.721 58.0618L344.171 94.8738C347.711 97.6795 346.881 103.264 342.677 104.92L287.632 126.608Z"
      />

      {/* Satélite 3 - Semicírculo (superior esquerdo) */}
      <path
        fill={fill}
        d="M347.104 134.993L348.724 134.199C350.589 133.416 352.589 133.01 354.612 133C356.634 132.991 358.639 133.38 360.51 134.145C362.382 134.91 364.084 136.036 365.522 137.458C366.958 138.881 368.1 140.572 368.882 142.436C378.934 166.374 382.763 192.464 380.011 218.278C377.259 244.092 368.017 268.791 353.143 290.075C351.985 291.732 350.511 293.144 348.806 294.231C347.102 295.319 345.2 296.059 343.208 296.412C341.218 296.765 339.176 296.723 337.202 296.287C335.228 295.851 333.357 295.031 331.7 293.874C330.043 292.716 328.629 291.244 327.541 289.54C326.454 287.837 325.712 285.936 325.359 283.946C325.006 281.957 325.049 279.917 325.485 277.943C325.921 275.971 326.741 274.102 327.9 272.446C339.794 255.421 347.186 235.665 349.387 215.018C351.588 194.371 348.528 173.503 340.489 154.356C339.025 150.879 338.89 146.987 340.11 143.417C341.329 139.847 343.818 136.849 347.104 134.993Z"
      />

      {/* Satélite 4 - Triângulo (inferior direito segundo) */}
      <path
        fill={fill}
        d="M272.778 356.22C268.892 358.51 263.984 355.745 263.932 351.239L263.247 292.115C263.196 287.6 268.056 284.724 271.993 286.941L323.641 316.024C327.578 318.241 327.634 323.886 323.741 326.18L272.778 356.22Z"
      />

      {/* Satélite 5 - Semicírculo (inferior esquerdo) */}
      <path
        fill={fill}
        d="M68.2976 136.153C81.4021 103.13 105.537 75.6549 136.607 58.3912C167.677 41.1275 203.768 35.1387 238.754 41.4411C240.769 41.7717 242.699 42.4994 244.429 43.5821C246.16 44.6647 247.658 46.0806 248.837 47.7473C250.015 49.4139 250.85 51.2981 251.291 53.2902C251.734 55.2823 251.776 57.3424 251.415 59.3507C251.053 61.359 250.295 63.2753 249.185 64.9882C248.075 66.7011 246.636 68.1763 244.95 69.3279C243.264 70.4795 241.366 71.2846 239.366 71.6963C237.367 72.108 235.304 72.1181 233.3 71.7258C205.308 66.6805 176.43 71.4709 151.571 85.2839C126.711 99.0968 107.401 121.081 96.9187 147.505C96.1946 149.413 95.098 151.158 93.6927 152.638C92.2873 154.118 90.6014 155.305 88.7329 156.128C86.8645 156.951 84.8509 157.395 82.8092 157.433C80.7675 157.471 78.7387 157.103 76.8407 156.35C74.9428 155.597 73.2136 154.476 71.7538 153.048C70.294 151.622 69.1328 149.919 68.3378 148.039C67.5427 146.159 67.1298 144.141 67.1229 142.1C67.116 140.059 67.5153 138.038 68.2976 136.153Z"
      />

      {/* Satélite 6 - Triângulo (inferior direito) */}
      <path
        fill={fill}
        d="M116.029 210.507C120.208 212.201 120.992 217.777 117.441 220.555L70.8523 257.005C67.2945 259.788 62.0556 257.676 61.4271 253.205L53.1824 194.538C52.554 190.066 57.0081 186.594 61.1955 188.291L116.029 210.507Z"
      />
    </svg>
  );
};
